import { useRef, useState } from "react";
import { api } from "@/lib/api";
import { fmt2 } from "@/lib/utils";
import { toast } from "sonner";
import { Search, ArrowLeft, ArrowRight, Truck, Box, ChefHat, PackagePlus, Timer } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { PageHeader, Panel } from "@/components/ui-bits";
import BarcodeScanner from "@/components/BarcodeScanner";

export default function Trace() {
  const [q, setQ] = useState("");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [res, setRes] = useState(null);
  const [loading, setLoading] = useState(false);
  const [elapsed, setElapsed] = useState(null);
  const startRef = useRef(null);

  const search = async (term) => {
    const t = (term ?? q).trim();
    if (!t && !fromDate && !toDate) {
      toast.error("Enter a search term or pick a production date range");
      return;
    }
    setLoading(true);
    startRef.current = performance.now();
    try {
      const qs = new URLSearchParams();
      if (t) qs.set("q", t);
      if (fromDate) qs.set("from_date", fromDate);
      if (toDate) qs.set("to_date", toDate);
      const { data } = await api.get(`/recall/search?${qs.toString()}`);
      setRes(data);
      setElapsed((performance.now() - startRef.current) / 1000);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Search failed");
    } finally {
      setLoading(false);
    }
  };

  const clearDates = () => {
    setFromDate("");
    setToDate("");
  };

  const logDrill = async () => {
    if (!res) return;
    const queryLabel = res.query
      || `Production ${res.from_date || "…"} → ${res.to_date || "…"}`;
    try {
      await api.post("/recall/drills", {
        query: queryLabel,
        result_counts: {
          intakes: res.intakes.length,
          batches: res.batches.length,
          packing: res.packing.length,
          dispatches: res.dispatches.length,
        },
        elapsed_seconds: elapsed,
        notes: "",
      });
      toast.success("Recall drill logged");
    } catch {
      toast.error("Could not log drill");
    }
  };

  return (
    <div>
      <PageHeader title="Trace & Recall" eyebrow="Quality · One-step back, one-step forward" />

      <Panel title="Search by batch number, label, supplier or production date range">
        <div className="space-y-3" data-testid="trace-search-row">
          <div className="flex gap-2 items-center">
            <Search className="h-5 w-5 text-stone-500" />
            <Input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && search()}
              placeholder="e.g. INT-20260201-0001, supplier batch, or recipe name"
              className="h-12 flex-1"
              data-testid="trace-input"
            />
            <BarcodeScanner onScan={(v) => { setQ(v); search(v); }} label="Scan barcode" />
            <Button onClick={() => search()} disabled={loading} className="h-12 px-6" data-testid="trace-search-button">
              {loading ? "Searching…" : "Search"}
            </Button>
          </div>
          <div className="flex flex-wrap items-end gap-3 pt-1 border-t border-border">
            <div className="flex flex-col">
              <label className="text-[11px] uppercase tracking-[0.18em] font-bold text-stone-500 mb-1">Production from</label>
              <Input
                type="date"
                value={fromDate}
                onChange={(e) => setFromDate(e.target.value)}
                className="h-11 w-44"
                data-testid="trace-from-date"
              />
            </div>
            <div className="flex flex-col">
              <label className="text-[11px] uppercase tracking-[0.18em] font-bold text-stone-500 mb-1">Production to</label>
              <Input
                type="date"
                value={toDate}
                onChange={(e) => setToDate(e.target.value)}
                className="h-11 w-44"
                data-testid="trace-to-date"
              />
            </div>
            {(fromDate || toDate) && (
              <Button
                variant="outline"
                size="sm"
                onClick={clearDates}
                data-testid="trace-clear-dates"
                className="h-11"
              >
                Clear dates
              </Button>
            )}
            <div className="text-xs text-stone-500 flex-1 min-w-[200px]">
              Leave the search box empty to pull every batch made in this date window — perfect for full-day or full-week recall drills.
            </div>
          </div>
        </div>
      </Panel>

      {res && (
        <div className="space-y-6 mt-6" data-testid="trace-results">
          <div className="flex items-center justify-between gap-3 flex-wrap bg-emerald-50/60 border border-emerald-200 rounded-lg p-4">
            <div className="flex items-center gap-3 text-sm text-emerald-900">
              <Timer className="h-4 w-4" />
              <span>
                {res.query ? (
                  <>
                    Result for <span className="font-mono font-semibold">{res.query}</span>
                    {(res.from_date || res.to_date) && (
                      <> &nbsp;between <span className="font-semibold tnum">{res.from_date || "…"}</span> and <span className="font-semibold tnum">{res.to_date || "…"}</span></>
                    )}
                  </>
                ) : (
                  <>
                    Production between <span className="font-semibold tnum">{res.from_date || "the beginning"}</span> and <span className="font-semibold tnum">{res.to_date || "today"}</span>
                  </>
                )}
                {" "}in <span className="tnum font-bold">{elapsed?.toFixed(2)}s</span> —{" "}
                {res.intakes.length} intake · {res.batches.length} batch ·{" "}
                {res.packing.length} pack · {res.dispatches.length} dispatch
              </span>
            </div>
            <Button size="sm" variant="outline" onClick={logDrill} data-testid="log-recall-drill">
              Log as recall drill
            </Button>
          </div>
          <Section icon={<PackagePlus className="h-5 w-5" />} title={`Intakes (${res.intakes.length})`} accent="text-emerald-700">
            {res.intakes.length === 0 ? <Empty /> : (
              <table className="w-full">
                <thead className="text-left text-xs uppercase tracking-wider text-stone-500">
                  <tr><th className="py-2">Internal</th><th>Supplier</th><th>Supplier batch</th><th>Material</th><th className="text-right">Remaining</th><th>Expiry</th></tr>
                </thead>
                <tbody>
                  {res.intakes.map((r) => (
                    <tr key={r.id} className="border-t border-border h-12">
                      <td className="font-mono text-sm">{r.internal_batch_code}</td>
                      <td>{r.supplier_name}</td>
                      <td className="font-mono text-sm">{r.supplier_batch}</td>
                      <td>{r.material_name}</td>
                      <td className="text-right tnum">{fmt2(r.remaining)} {r.unit}</td>
                      <td className="text-stone-600 text-sm tnum">{r.expiry_date?.slice(0,10)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </Section>

          <div className="flex justify-center"><ArrowRight className="h-5 w-5 text-stone-400" /></div>

          <Section icon={<ChefHat className="h-5 w-5" />} title={`Production batches (${res.batches.length})`}>
            {res.batches.length === 0 ? <Empty /> : (
              <div className="space-y-3">
                {res.batches.map((b) => (
                  <div key={b.id} className="border border-border rounded-lg p-4">
                    <div className="flex items-center justify-between flex-wrap gap-2">
                      <div>
                        <div className="font-mono text-sm">{b.batch_code}</div>
                        <div className="font-semibold">{b.recipe_name}</div>
                      </div>
                      <div className="text-sm text-stone-600">Stage <span className="font-semibold">{b.stage}</span> · Produced {fmt2(b.quantity_produced)}</div>
                    </div>
                    {b.consumptions?.length > 0 && (
                      <div className="mt-2 text-xs text-stone-600 space-y-1" data-testid={`batch-ingredients-${b.id}`}>
                        <div className="font-semibold uppercase tracking-wider text-[10px] text-stone-500">
                          <ArrowLeft className="inline h-3 w-3 mr-1" />
                          Ingredients used
                        </div>
                        {/* One line per consumption so the operator (and
                            auditor) can see exactly which supplier batch
                            went into this product — required for a SALSA
                            / BRCGS recall trail. */}
                        {b.consumptions.map((c) => (
                          <div key={`${c.intake_id}-${c.material_id}`} className="flex flex-wrap gap-x-3 gap-y-0.5">
                            <span className="font-medium text-stone-800">{c.material_name}</span>
                            <span className="font-mono text-stone-700">Int: {c.internal_batch_code}</span>
                            {c.supplier_batch && (
                              <span className="font-mono text-stone-700">Sup: {c.supplier_batch}</span>
                            )}
                            {c.supplier_name && (
                              <span className="text-stone-600">from {c.supplier_name}</span>
                            )}
                            <span className="tnum text-stone-600">
                              {fmt2(c.quantity)} {c.unit || ""}
                            </span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </Section>

          <div className="flex justify-center"><ArrowRight className="h-5 w-5 text-stone-400" /></div>

          <Section icon={<Box className="h-5 w-5" />} title={`Packing labels (${res.packing.length})`}>
            {res.packing.length === 0 ? <Empty /> : (
              <table className="w-full">
                <thead className="text-left text-xs uppercase tracking-wider text-stone-500">
                  <tr><th className="py-2">Label</th><th>Batch</th><th>Product</th><th className="text-right">Pack size</th><th className="text-right">Remaining</th></tr>
                </thead>
                <tbody>
                  {res.packing.map((p) => (
                    <tr key={p.id} className="border-t border-border h-12">
                      <td className="font-mono text-sm">{p.label_code}</td>
                      <td className="font-mono text-sm text-stone-600">{p.batch_code}</td>
                      <td>{p.recipe_name}</td>
                      <td className="text-right tnum">{fmt2(p.pack_size)} {p.pack_unit}</td>
                      <td className="text-right tnum">{fmt2(p.remaining_packs)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </Section>

          <div className="flex justify-center"><ArrowRight className="h-5 w-5 text-stone-400" /></div>

          <Section icon={<Truck className="h-5 w-5" />} title={`Customer dispatches (${res.dispatches.length})`} accent="text-amber-700">
            {res.dispatches.length === 0 ? <Empty /> : (
              <div className="space-y-3">
                {res.dispatches.map((d) => (
                  <div key={d.id} className="border border-border rounded-lg p-4">
                    <div className="flex items-center justify-between flex-wrap gap-2">
                      <div>
                        <div className="font-mono text-sm">{d.dispatch_code}</div>
                        <div className="font-semibold">{d.customer_name}</div>
                      </div>
                      <div className="text-sm text-stone-600">{d.dispatch_date?.slice(0,10)} · {d.driver} · {d.vehicle}</div>
                    </div>
                    <div className="mt-2 text-xs text-stone-600">{d.items?.map((it)=>`${it.label_code} ×${it.quantity_packs}`).join(", ")}</div>
                  </div>
                ))}
              </div>
            )}
          </Section>
        </div>
      )}
    </div>
  );
}

function Section({ icon, title, accent="text-primary", children }) {
  return (
    <div className="panel">
      <div className="panel-header">
        <div className={`font-display font-semibold flex items-center gap-2 ${accent}`}>{icon} {title}</div>
      </div>
      <div className="panel-body">{children}</div>
    </div>
  );
}
function Empty() {
  return <div className="text-sm text-stone-500 py-6 text-center">No matches in this stage.</div>;
}
